import Axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'

import { env } from '@/config/env-config'
import { getAuthHeader } from '@/shared/auth/stores/authStore'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/**
 * Dedicated client for binary downloads. The shared apiClient response interceptor does
 * `return response.data.data` unconditionally, which destroys a binary body — so this instance
 * deliberately registers NO response interceptor and returns the raw Axios response. It only
 * attaches the Bearer token and the xlsx Accept header (API Gateway returns binary only when the
 * request Accept matches a configured BinaryMediaType).
 */
const downloadClient = Axios.create({ baseURL: env.API_URL, responseType: 'blob' })

downloadClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const authHeader = getAuthHeader()
  if (authHeader && !config.headers.Authorization) {
    config.headers.Authorization = authHeader
  }
  config.headers.Accept = XLSX_MIME
  return config
})

/** Parse a Content-Disposition header into a filename, falling back when absent/unparseable. */
export function filenameFromDisposition(header: string | undefined, fallback: string): string {
  if (!header) {
    return fallback
  }
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''))
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim() || fallback
}

/** Save a Blob to disk via a transient anchor (object URL revoked after the click). */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Defer revoke so the download has captured the blob before the URL is invalidated (some browsers race a sync revoke).
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * GET a binary file and save it. Throws on HTTP error (caller renders the message). On a 413 the
 * caller should tell the user to narrow filters (the issues export row cap).
 */
export async function downloadFile(url: string, fallbackName: string, config?: AxiosRequestConfig): Promise<void> {
  const res = await downloadClient.get<Blob>(url, config)
  const filename = filenameFromDisposition(res.headers['content-disposition'] as string | undefined, fallbackName)
  triggerBrowserDownload(res.data, filename)
}
